// 🔧 lgv_features.js
// ✅ Purpose: Central source of truth for LGV core features and paid revenue add-ons
// ✅ Features:
// - Defines the standard Event Package included features
// - Defines revenue add-ons that can be purchased or admin-enabled per event
// - Keeps pricing/add-on labels consistent across landing, request_event, event.js, nav, and future checkout pages
// - Fully block-commented for clarity and future devs

export const LGV_EVENT_PACKAGE = {
  key: "event_package",
  name: "LGV Event Package",
  price: 99,
  priceDisplay: "$99",
  priceNote: "per event",
  description:
    "The complete Let's Go Vote event package for voting, entries, sponsors, volunteers, maps, scheduling, public registration, SEO, reporting, and manager tools.",
};

export const LGV_CORE_FEATURES = [
  {
    key: "voting",
    icon: "🗳️",
    name: "Voting",
    description:
      "QR code voting, People’s Choice voting, ranked choice voting, judged points, scorecards, ballots, and voter tools.",
  },
  {
    key: "entries",
    icon: "🚗",
    name: "Entries & Categories",
    description:
      "Create categories, manage entries, print QR codes, and organize event participants.",
  },
  {
    key: "leaderboard",
    icon: "🏆",
    name: "Leaderboards & Winners",
    description:
      "Show live leaderboards, schedule winner reveals, and display final results.",
  },
  {
    key: "volunteers",
    icon: "🙋",
    name: "Volunteers",
    description:
      "Volunteer opportunities, shift signup, time slots, and volunteer management.",
  },

  {
    key: "maps",
    icon: "🗺️",
    name: "Maps",
    description:
      "Event map support for sponsors, vendors, bathrooms, registration, and event locations.",
  },
  {
    key: "schedule",
    icon: "📅",
    name: "Scheduling",
    description:
      "Public schedule items, times, locations, and event agenda information.",
  },
  {
    key: "seo",
    icon: "🔎",
    name: "SEO & Public Pages",
    description:
      "Public event pages, search engine visibility, social previews, and marketing support.",
  },
  {
    key: "reporting",
    icon: "📊",
    name: "Reporting & Auditing",
    description:
      "Results exports, vote review tools, activity review, and event reporting.",
  },
];

export const LGV_REVENUE_ADDONS = [
  {
    key: "passport",
    icon: "🧭",
    name: "Engagement Passport",
    price: 49,
    priceDisplay: "$49",
    description:
      "Increase attendee participation with passport challenges, points, rewards, scavenger hunts, sponsor engagement, vendor engagement, and Event Intelligence analytics.",
  },
  {
    key: "auction",
    icon: "🏷️",
    name: "Silent Auction",
    price: 49,
    priceDisplay: "$49",
    description:
      "Auction items, public bidding, outbid notifications, winner tracking, checkout support, and auction results.",
  },
  {
    key: "tickets",
    icon: "🎟️",
    name: "Ticket Sales",
    price: 49,
    priceDisplay: "$49",
    description:
      "Sell event tickets online, track orders, and support event check-in workflows.",
  },
  {
    key: "vendorPayments",
    icon: "🛍️",
    name: "Vendor Payments",
    price: 29,
    priceDisplay: "$29",
    description:
      "Collect vendor fees online using the event organizer’s Stripe Connect account.",
  },
  {
    key: "sponsorPayments",
    icon: "⭐",
    name: "Sponsor Payments",
    price: 29,
    priceDisplay: "$29",
    description:
      "Collect sponsorship fees online using sponsor levels and the event organizer’s Stripe Connect account.",
  },
  {
    key: "store",
    icon: "🛒",
    name: "Event Merch Store",
    price: 29,
    priceDisplay: "$29",
    description:
      "Sell event merchandise, manage products, track orders, and support pickup or shipping workflows.",
  },
];

export const SOCIAL_ENGINE_POWER_UP = {
  key: "social",
  icon: "📣",
  name: "Social Promotion",
  price: 49,
  priceDisplay: "$49",
  description:
    "Create event promotion drafts, social content, publishing schedules, and campaign assets for this event.",
};

export const LGV_OPTIONAL_POWER_UPS = [
  ...LGV_REVENUE_ADDONS,
  SOCIAL_ENGINE_POWER_UP,
];

// Complete set of event capabilities that administrators can activate or
// deactivate from the event editor. Content Power Ups are separate from the
// payment-specific Power Ups even when they work together (for example,
// Sponsors and Sponsor Payments).
export const LGV_POWER_UPS = [
  {
    key: "sponsors",
    icon: "⭐",
    name: "Sponsors",
    priceDisplay: "Power Up",
    description:
      "Sponsor levels, sponsor profiles, public sponsor visibility, awards, and map locations.",
  },
  {
    key: "vendors",
    icon: "🛍️",
    name: "Vendors",
    priceDisplay: "Power Up",
    description:
      "Vendor profiles, public vendor visibility, featured vendors, booths, and map locations.",
  },
  ...LGV_OPTIONAL_POWER_UPS,
];

export const DEFAULT_PURCHASED_ADDONS = {
  sponsors: false,
  vendors: false,
  auction: false,
  tickets: false,
  vendorPayments: false,
  sponsorPayments: false,
  store: false,
  passport: false,
  promotion: false,
  social: false,
};

export const getDefaultPurchasedAddOns = () => ({
  ...DEFAULT_PURCHASED_ADDONS,
});
